<template>
  <div class="orgdetails" v-if="user">
    <v-container class="pt-10">
      <div>
        <v-row class="px-3 pt-1 mb-6">
          <v-responsive class="overflow-visible">
            <h1 class="subheading grey--text">
              {{ user.email }}
            </h1>
            <p class="grey--text">
              {{ user.name }} <span class="caption pl-2">{{ user.id }}</span>
            </p>
          </v-responsive>
          <v-btn icon color="primary" @click.prevent="dialogEditUser = true">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </v-row>

        <v-row class="px-3 pt-1 mb-6">
          <DataSummaryTranscripts
            orgId=""
            :userId="this.$route.params.id"
            :key="`${datesrange[0]}+${datesrange[1]}+'1'`"
          />
        </v-row>

        <UsageTimeChart
          orgId=""
          :userId="this.$route.params.id"
          :key="`${datesrange[0]}+${datesrange[1]}+'5'`"
        />

        <!-- <TranscTable orgId="" :userId="this.$route.params.id" :key="`${datesrange[0]}+${datesrange[1]}+'2'`" /> -->

        <v-row class="px-3 pt-1 mb-6">
          <v-col cols="12" sm="12" md="12">
            <v-card>
              <v-card-title>
                Last 100 Transcriptions
                <v-spacer></v-spacer>
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search"
                  single-line
                  hide-details
                ></v-text-field>
              </v-card-title>
              <v-data-table
                v-if="transcripts"
                :items-per-page="5"
                :headers="headers"
                :items="transcripts"
                :search="search"
                @click:row="seeTranscDetails"
              >
                <template v-slot:[`item.createdAt`]="{ item }">
                  <span>{{ formatDate(item.createdAt) }}</span>
                </template>
                <template v-slot:[`item.metadata.audioDuration`]="{ item }">
                  <span>{{ formatLength(item.metadata.audioDuration) }}</span>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="px-3 pt-1 mb-6">
          <v-col cols="12" sm="12" md="12">
            <v-card>
              <v-card-title>
                Special features
                <v-spacer></v-spacer>
              </v-card-title>
              <SpecialFeaturesUser
                :featuresAlreadyEnabled="this.user.features"
                :userId="this.user.id"
                :key="JSON.stringify(this.user)"
              />
            </v-card>
          </v-col>
        </v-row>

        <v-row class="px-3 pt-1 mb-6">
          <v-col cols="12" sm="12" md="12">
            <v-card>
              <v-card-title class="pb-0">
                Misc
                <v-spacer></v-spacer>
              </v-card-title>
              <UserIsEnterprise
                :user="this.user"
                :key="JSON.stringify(this.user)"
              />
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-container>

    <!-- Dialog to add a user -->
    <!-- <v-dialog v-model="dialogAddUserToOrg" persistent max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">Add New User to <span class="font-weight-medium">{{org.name}}</span></span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" sm="12" md="6">
                <v-text-field label="Email*" v-model="newUserEmail" required></v-text-field>
              </v-col>
              <v-col cols="12" sm="12" md="6">
                <v-text-field label="Full Name*" v-model="newUserName" required></v-text-field>
              </v-col>
            </v-row>
          </v-container>
          <small>*indicates required field</small>
          <p>This creates a user and automatically adds it to the {{org.name}} organization.</p>
          <p>Pass: welcomelstn </p>
        </v-card-text>
        <v-card-actions>
          <p v-if="feedback" class="red--text pl-5">{{feedback}}</p>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dialogAddUserToOrg = false">Cancel</v-btn>
          <v-btn color="primary" @click="addUserToOrg">Add User to Org</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    -->

    <!-- Dialog to edit a user -->
    <v-dialog v-model="dialogEditUser" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">Edit User</span>
        </v-card-title>
        <v-card-text>
          <p>
            Important: all the actions below are immediate and cannot be
            reverted. Please use them with caution
          </p>
          <v-container>
            <v-row>
              <v-col cols="12" sm="12" md="12">
                <v-btn class="primary" @click.stop="resetUserPass"
                  >Hard Reset Password</v-btn
                >
                <p>New pass is set to: {{ newUserPass }}</p>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <Snackbar
      :text="snackbarText"
      :isopen="snackbarIsOpen"
      :color="snackbarColor"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  fb,
  usersCollection,
  transcriptsCollection,
} from "@/plugins/firebase.js";
import moment from "moment";

export default {
  name: "OrganizationDetails",
  title: "OrgDetails",

  components: {
    DataSummaryTranscripts: () =>
      import("@/components/data/DataSummaryTranscripts"),
    // TranscTable: () => import("@/components/data/TranscTable"),
    UsageTimeChart: () =>
      import("@/components/charts/UsageTimeChart"),
    SpecialFeaturesUser: () => import("@/components/misc/SpecialFeaturesUser"),
    UserIsEnterprise: () => import("@/components/misc/UserIsEnterprise"),
    Snackbar: () => import("@/components/Snackbar"),
  },

  data() {
    return {
      user: null,
      org: null,
      transcripts: null,

      search: "",
      headers: [
        {
          text: "Name",
          align: "start",
          filterable: true,
          value: "name",
        },
        { text: "Status", value: "status.progress" },
        { text: "Date", value: "createdAt" },
        { text: "Length (mins)", value: "metadata.audioDuration" },
        { text: "Lang", value: "metadata.languageCodes[0]" },
        { text: "Archived", value: "archive" },
        // { text: 'Link', value: 'playbackGsUrl' }
      ],

      dialogAddUserToOrg: false,
      newUserEmail: null,
      newUserName: null,
      feedback: null,
      newUserPass: "welcomelstn",

      dialogEditUser: false,

      snackbarIsOpen: false,
      snackbarColor: "success",
      snackbarText: "",
    };
  },
  computed: {
    ...mapGetters({
      datesrange: "datesrange",
    }),
  },
  methods: {
    seeTranscDetails(value) {
      this.$router.push(`/transcription/${value.id}`);
      // console.log(value.id)
    },
    async fetchUserInfo(id) {
      // let doc = await usersCollection.doc(id).get();

      await usersCollection.doc(id).onSnapshot((doc) => {
        // console.log("Current data: ", doc.data());
        let userinfo = doc.data();
        userinfo.id = doc.id;
        this.user = userinfo;
      });
    },
    async fetchUsersTranscriptions(id) {
      const doc = await transcriptsCollection
        .where("userId", "==", id)
        .orderBy("createdAt", "desc")
        .limit(100)
        .get();

      let transc = [];
      await doc.forEach(async (doc) => {
        let singleTrans = doc.data();
        singleTrans.id = doc.id;
        transc.push(singleTrans);
      });

      this.transcripts = transc;
    },
    formatDate(value) {
      let a = moment(value.toDate()).format("l");
      return a;
    },
    formatLength(value) {
      let a = (value / 60).toFixed(0);
      return a;
    },
    async resetUserPass() {
      const forceEditPassword = fb
        .app()
        .functions("europe-west1")
        .httpsCallable("adminResetUserPassword");
      let data = {
        uid: this.$route.params.id,
        newpassword: this.newUserPass,
      };
      try {
        this.dialogEditUser = false;
        await forceEditPassword(data);
        this.snackbarIsOpen = true;
        this.snackbarText = "password changed";
      } catch (error) {
        console.log(error);
      }
    },
  },
  mounted() {
    this.fetchUsersTranscriptions(this.$route.params.id);
  },
  created() {
    this.fetchUserInfo(this.$route.params.id);
  },
};
</script>

<style scoped>
.start-button {
  max-width: 20px;
}
</style>